/**
 * **图渲染的 DOM 契约** —— 「谁来画节点图」必须提供什么。
 *
 * ## 为什么要把它写成一份契约（而不是继续散在选择器里）
 *
 * 实测（`package-boundaries.md` 附录 A）：**26 处 JS 在选择器里写死了
 * `vue-flow__*`**，而它们**不在任何 import 清单里** ——
 * `grep "@vue-flow"` 完全看不见。所以"我们到底绑得多死"这件事，
 * 靠读 import 是量不出来的。
 *
 * 这一层是**换渲染层时唯一要重新实现的东西**。写成一个模块之后：
 *
 *   · 换实现 = 重新实现 `FLOW_DOM` 这张表（+ 那两条属性名），
 *     而不是满仓库找字符串；
 *   · `verify:layers` 有一条静态守卫：**JS/模板里 `vue-flow__` 只许出现在
 *     本文件**（CSS 除外，见下）+ **这张表里每个键都真的有人用** ——
 *     前者防绕过、后者防它长成一堆没人读的常量。
 *
 * ## ★ 2027：这一层**真的用上了**
 *
 * `graph/canvas/` 是我们自己写的画布（Vue Flow 已经删掉了）。那次切换
 * 就是照着这份契约做的 —— 结果是：
 *
 * ```text
 *   改名（vue-flow__* → np-graph__*）：动这张表 + 四个 CSS 文件
 *   26 处调用点：                       一行没动（它们走常量）
 *   两个真适配层文件：                  只有它们 import 引擎，最后一起删掉
 * ```
 *
 * ## 两层，别混
 *
 * ```text
 *   ① 我们自己的钩子（与渲染层无关）
 *        data-node-id / data-node-type     谁写：NpNode / NpNodeBox   谁读：命中测试、悬停提示
 *        data-category / data-render       谁写：NpNode               谁读：主题（按分类/形态上色）
 *        data-row-kind                     谁写：六种行组件            谁读：主题
 *        data-socket-type（+ .np-socket）  谁写：NpSocket             谁读：主题（端口配色，语义约束）
 *        np-widget--<kind>                 谁写：各控件               谁读：主题
 *      ⚠️ 这些**不随渲染层变**：主题就是靠它们工作的（`package-boundaries.md` §4）。
 *         它们没有常量表 —— 读它们的是 **CSS 选择器**（TS 常量帮不到 CSS），
 *         而"真的渲染出来了没有"由 `verify:themedom` 一条条断言守着。
 *
 *   ② 画布必须提供的  ↓ 下面那张 FLOW_DOM 表（`graph/canvas/` 实现它）
 *        谁写：画布                     谁读：视口换算、命中测试、剪线、小地图、悬停提示
 *        ⚠️ 换渲染层时这一层要**整体重新实现**（今天换过一次：
 *        Vue Flow → 我们自己的 `graph/canvas/`）
 * ```
 *
 * ## ⚠️ 一个仍然没做的下一步
 *
 * 层②里有三处**本可以是层①**：`.np-graph__handle[data-nodeid][data-handleid]`
 * （命中测试 / 悬停提示 / 连线手势）。那些端口是**我们自己渲染的**
 * （`NpCanvasHandle.vue`），所以完全可以在它身上挂 `data-np-node` /
 * `data-np-handle` —— 那三处就不再需要"先找 class 再读属性"两步。
 *
 * 现在**没做**：命中测试在 happy-dom 里没有布局可验（悬停、长按都靠真实
 * 坐标），改它的回归风险大于收益。换渲染层那一步已经过去了，
 * 而这一步的收益只是"少一次 `closest`" —— 所以留着，别当已经做完。
 *
 * ## CSS 那半边不在守卫范围内
 *
 * `np-canvas.css` / `theme.css` / `playground.css` 里有同一批选择器 ——
 * 那是**给画布的 DOM 上样式**（端口圆点、连线、底纹、小地图）。
 * 它和这张表是**一份契约的两半**，一起改；但判据不同，所以不共用机制
 * （CSS 是"长什么样"，JS 是"谁在哪"）。
 */

/**
 * **画布必须提供的 class**（今天由我们自己的 `graph/canvas/` 提供）。
 *
 * ⚠️ 表里存的是 **class 名**，不是选择器 —— **不许带点、不许带空格**。
 * 用的时候调用点自己拼：`` `.${FLOW_DOM.node}` ``。
 *
 * ⚠️ 这条规矩是**用一次真机事故换来的**：`minimapSvg` 原来是
 * `'.vue-flow__minimap svg'`（选择器形状），改名时我写成了
 * `'np-graph__minimap'`，而有一处读者**直接把常量当选择器用** →
 * `querySelector('np-graph__minimap')` 抛 `DOMException`，
 * 每次 pointerdown 都炸 → 用户看到的是「小地图完全点不动」。
 * `verify:layers` 现在有一条守卫：表里的值必须匹配 `^[a-z][a-z0-9_-]*$`。
 *
 * ⚠️ 表里每一个键都必须**真的有人读** —— `verify:layers` 会数。
 *
 * ⚠️ **名字是 2027 改的**（原来是 `vue-flow__*`）：那时候它是"引擎必须
 * 提供的名字"，现在画布是我们自己的，名字也该是自己的 ——
 * 留着一套 `vue-flow__` 只会让人以为还有那个依赖。
 * 改名只动了这张表 + 四个 CSS 文件；**26 处调用点一行没动**
 * （它们本来就走这些常量）—— 这就是当初把它抽出来的回报。
 */
export const FLOW_DOM = {
  /** 一个节点（外面那层定位盒）。画布在它上面挂 `data-id` */
  node: 'np-graph__node',
  /** 一个端口（层②，见文件头「已知的下一步」） */
  handle: 'np-graph__handle',
  /** 一条边（外面那层 `<g>`）。画布在它上面挂 `data-id` */
  edge: 'np-graph__edge',
  /** 边的可见路径 —— 剪线靠**采样它**拿到真实折线 */
  edgePath: 'np-graph__edge-path',
  /** 视口变换层 —— 屏幕坐标 ↔ 画布坐标的换算基准 */
  viewport: 'np-graph__viewport',
  /** 小地图那棵 `<svg>`（手势要拿它的 viewBox 与元素尺寸） */
  minimapSvg: 'np-graph__minimap',
} as const

/**
 * 端口的**标识属性**：`data-nodeid` / `data-handleid`。
 *
 * ⚠️ 注意拼写：**没有连字符**（不是 `data-node-id`）——
 * 节点根元素上那对是 `data-node-id` / `data-node-type`（见下面文件头的
 * 层①表），两者长得极像，所以都在契约里点名。
 *
 * ⚠️ 这两个名字是从 Vue Flow 那边**继承下来的**（它们当年是引擎挂的）。
 * 之所以没顺手改成 `data-np-*`：5 处读者都走常量，改名是安全的；
 * 但**没有任何好处** —— 名字本身不携带"谁挂的"这个信息，而常量已经
 * 把这件事说清楚了。留着的唯一代价是"看着像引擎的"，所以这里写明白。
 */
export const HANDLE_NODE_ATTR = 'data-nodeid'
export const HANDLE_ID_ATTR = 'data-handleid'
